import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FileText, Calendar, Wrench, ClipboardList, ShoppingBag, LogOut, Settings, ExternalLink } from "lucide-react";

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [assistance, setAssistance] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [appointmentsRes, assistanceRes, quotesRes, ordersRes] = await Promise.all([
          axios.get(`${API}/appointments`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/assistance`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setAppointments(appointmentsRes.data);
        setAssistance(assistanceRes.data);
        setQuotes(quotesRes.data);
        setOrders(ordersRes.data);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleOrdersClick = () => {
    window.open("https://tracciamento.colaciccolegno.com", "_blank");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "text-yellow-500 bg-yellow-500/10";
      case "confirmed": return "text-emerald-500 bg-emerald-500/10";
      case "completed": return "text-blue-500 bg-blue-500/10";
      case "cancelled": return "text-red-500 bg-red-500/10";
      default: return "text-white/60 bg-white/10";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pending": return "In attesa";
      case "confirmed": return "Confermato";
      case "completed": return "Completato";
      case "cancelled": return "Annullato";
      default: return status;
    }
  };

  return (
    <div className="min-h-screen bg-black pb-8" data-testid="profile-page">
      {/* Profile Header */}
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center gap-6">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 p-1">
            <div className="w-full h-full rounded-full bg-zinc-900 flex items-center justify-center">
              <span className="text-2xl font-bold text-emerald-500">
                {user?.name?.[0] || user?.email?.[0] || "U"}
              </span>
            </div>
          </div>

          {/* Stats */}
          <div className="flex-1 grid grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-xl font-bold text-white">{appointments.length}</p>
              <p className="text-xs text-white/60">Appuntamenti</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-white">{orders.length}</p>
              <p className="text-xs text-white/60">Ordini</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-white">{quotes.length}</p>
              <p className="text-xs text-white/60">Preventivi</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-bold text-white">{assistance.length}</p>
              <p className="text-xs text-white/60">Assistenze</p>
            </div>
          </div>
        </div>

        {/* User Info */}
        <div className="mt-4">
          <h1 className="text-lg font-medium text-white">
            {user?.name} {user?.surname}
          </h1>
          <p className="text-sm text-white/60">{user?.email || user?.phone}</p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-4">
          <Button
            variant="outline"
            className="flex-1 border-white/20 text-white hover:bg-white/10"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Esci
          </Button>
          {user?.is_admin && (
            <Button
              onClick={() => navigate("/admin")}
              className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400"
              data-testid="admin-panel-btn"
            >
              <Settings size={16} className="mr-2" />
              Admin
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="appointments" className="w-full">
        <TabsList className="w-full bg-zinc-900 rounded-none border-b border-white/10 h-auto p-0 grid grid-cols-5">
          <TabsTrigger 
            value="appointments" 
            className="py-3 rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-emerald-500 flex flex-col gap-1"
            data-testid="tab-appointments"
          >
            <Calendar size={18} />
            <span className="text-[10px]">Prenotazioni</span>
          </TabsTrigger>
          <TabsTrigger 
            value="orders"
            className="py-3 rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-emerald-500 flex flex-col gap-1"
            data-testid="tab-orders"
            onClick={handleOrdersClick}
          >
            <ShoppingBag size={18} />
            <span className="text-[10px]">Ordini</span>
          </TabsTrigger>
          <TabsTrigger 
            value="quotes"
            className="py-3 rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-emerald-500 flex flex-col gap-1"
            data-testid="tab-quotes"
          >
            <ClipboardList size={18} />
            <span className="text-[10px]">Preventivi</span>
          </TabsTrigger>
          <TabsTrigger 
            value="assistance"
            className="py-3 rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-emerald-500 flex flex-col gap-1"
            data-testid="tab-assistance"
          >
            <Wrench size={18} />
            <span className="text-[10px]">Assistenze</span>
          </TabsTrigger>
          <TabsTrigger 
            value="documents"
            className="py-3 rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-emerald-500 flex flex-col gap-1"
            data-testid="tab-documents"
          >
            <FileText size={18} />
            <span className="text-[10px]">Documenti</span>
          </TabsTrigger>
        </TabsList>

        {/* Appointments Tab */}
        <TabsContent value="appointments" className="p-4 space-y-3">
          {appointments.length === 0 ? (
            <div className="text-center py-12 text-white/60">
              <Calendar size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nessun appuntamento</p>
              <Button 
                onClick={() => navigate("/prenotazione")}
                className="mt-4 bg-emerald-500 text-black"
              >
                Prenota ora
              </Button>
            </div>
          ) : (
            appointments.map((apt) => (
              <div key={apt.id} className="bg-zinc-900 p-4 rounded-lg border border-white/10">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-white">{apt.product_type}</p>
                    <p className="text-sm text-white/60">{apt.date} alle {apt.time}</p>
                    {apt.quantity && (
                      <p className="text-sm text-emerald-500">Quantità: {apt.quantity} {apt.quantity_unit}</p>
                    )}
                    {apt.notes && <p className="text-sm text-white/40 mt-1">{apt.notes}</p>}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(apt.status)}`}>
                    {getStatusLabel(apt.status)}
                  </span>
                </div>
              </div>
            ))
          )}
        </TabsContent>

        {/* Orders Tab - Opens external link */}
        <TabsContent value="orders" className="p-4">
          <div className="text-center py-12 text-white/60">
            <ShoppingBag size={48} className="mx-auto mb-4 opacity-50" />
            <p className="mb-4">Traccia i tuoi ordini sul portale dedicato</p>
            <Button 
              onClick={handleOrdersClick}
              className="bg-emerald-500 text-black hover:bg-emerald-400"
            >
              <ExternalLink size={16} className="mr-2" />
              Vai al Tracciamento Ordini
            </Button>
          </div>
        </TabsContent>

        {/* Quotes Tab */}
        <TabsContent value="quotes" className="p-4 space-y-3">
          {quotes.length === 0 ? (
            <div className="text-center py-12 text-white/60">
              <ClipboardList size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nessun preventivo richiesto</p>
              <Button 
                onClick={() => navigate("/preventivi")}
                className="mt-4 bg-emerald-500 text-black"
              >
                Richiedi preventivo
              </Button>
            </div>
          ) : (
            quotes.map((quote) => (
              <div key={quote.id} className="bg-zinc-900 p-4 rounded-lg border border-white/10">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-white">{quote.category}</p>
                    <p className="text-sm text-white/60 line-clamp-2">{quote.description}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(quote.status)}`}>
                    {getStatusLabel(quote.status)}
                  </span>
                </div>
              </div>
            ))
          )}
        </TabsContent>

        {/* Assistance Tab */}
        <TabsContent value="assistance" className="p-4 space-y-3">
          {assistance.length === 0 ? (
            <div className="text-center py-12 text-white/60">
              <Wrench size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nessuna richiesta di assistenza</p>
              <Button 
                onClick={() => navigate("/assistenza")}
                className="mt-4 bg-emerald-500 text-black"
              >
                Richiedi assistenza
              </Button>
            </div>
          ) : (
            assistance.map((req) => (
              <div key={req.id} className="bg-zinc-900 p-4 rounded-lg border border-white/10">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-white">{req.product_name}</p>
                    <p className="text-xs text-white/50">{req.product_brand} - {req.purchase_year}</p>
                    <p className="text-sm text-white/60 line-clamp-2 mt-1">{req.issue_description}</p>
                    {req.media_files && req.media_files.length > 0 && (
                      <p className="text-xs text-emerald-500 mt-1">{req.media_files.length} file allegati</p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(req.status)}`}>
                    {getStatusLabel(req.status)}
                  </span>
                </div>
              </div>
            ))
          )}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="p-4">
          <div className="text-center py-12 text-white/60">
            <FileText size={48} className="mx-auto mb-4 opacity-50" />
            <p>I documenti saranno disponibili dopo il primo ordine</p>
            <p className="text-sm mt-2">Contratti, fatture e ordini appariranno qui</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProfilePage;
